import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { TradeFilterStateKey, TradeGridStateKey } from '@approot/models';
import {
  AgGridService,
  AlertsService,
  EntityMapperService,
  RfqService,
  StrategyDetailService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { sleep, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColumnApi,
  ColumnState,
  GridApi,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import dayjs from 'dayjs';
import { Debounce } from 'lodash-decorators/debounce';
import { Throttle } from 'lodash-decorators/throttle';
import { interval, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

export interface HistoricalTradesMeta {
  datetime: string;
  timezone: string;
}

@Component({
  selector: 'app-trades-grid',
  templateUrl: './trades-grid.component.html',
  styleUrls: ['./trades-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradesGridComponent implements OnDestroy {
  @Input() hasTradesBeenClicked = false;
  @Output() public tradesLoaded = new EventEmitter<any[]>();
  @Output() private hideTradesTab = new EventEmitter<true>();
  public gridApi: GridApi;
  public columnApi: ColumnApi;
  public sideBarDef: SideBarDef;
  public isLoadHistoricalTradesOpen = false;
  public isTradeManagerOpen = false;
  public selectedTrades = [];
  public isHistoricalTrades = false;
  public historicalTradesMeta: HistoricalTradesMeta = null;
  public maxTrades = 25;
  public isLoadPortfolioOpen = false;
  public enableDealSTP: boolean;
  public numberOfTradesDisplayed: number;
  private lastUpdatedAt = new Date();
  private sub = new SubscriptionSink();
  private trades: any[] = [];
  private strategyDetailService = inject(StrategyDetailService);
  private userStorageService = inject(UserStorageService);
  private firstLoad = true;

  constructor(
    private tradesCaptureService: TradesCaptureService,
    private agGridService: AgGridService,
    private rfqService: RfqService,
    private alertsService: AlertsService,
    changeDetectorRef: ChangeDetectorRef,
    private entityMapperService: EntityMapperService
  ) {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.sub.sink = this.tradesCaptureService.tradeDone$.subscribe(async () => {
      // Allow a few secs for the trade to be processed
      await sleep(2000);
      this.loadTrades();
    });

    this.sub.sink = interval(1_000).subscribe(() => {
      changeDetectorRef.markForCheck();
    });

    this.enableDealSTP = this.tradesCaptureService.dvEntities.enableDealSTP;
  }

  public ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  @Throttle(1_000)
  public get lastUpdatedAtStr(): string {
    return dayjs(this.lastUpdatedAt).fromNow();
  }

  public get tradeHeaders() {
    return this.tradesCaptureService.tradesGridHeaders;
  }

  public onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    this.gridApi.setRowData([]);
    this.loadTrades();
    this.sub.sink = this.tradesCaptureService.trades
      .pipe(finalize(() => this.gridApi.hideOverlay()))
      .subscribe(trades => {
        this.lastUpdatedAt = new Date();
        this.trades =
          this.entityMapperService.uncleanConfidentialFields(trades);
        this.gridApi.setRowData(this.trades);
        this.applyFilterModel();
      });

    const columnState =
      this.userStorageService.getItem<ColumnState[]>(TradeGridStateKey);
    if (columnState) {
      try {
        params.columnApi.applyColumnState({
          state: columnState,
          applyOrder: true
        });
      } catch (e) {
        console.error('Unable to load column state');
      }
    }
  }

  public applyFilterModel() {
    if (this.firstLoad) {
      this.firstLoad = false;
      return;
    }
    const filterModel = this.userStorageService.getItem(TradeFilterStateKey);
    if (filterModel) {
      try {
        this.gridApi.setFilterModel(filterModel);
        this.gridApi.onFilterChanged();
      } catch (e) {
        console.error('Unable to load filter model');
      }
    }
  }

  public onSelectionChanged() {
    this.selectedTrades = this.gridApi.getSelectedRows();
  }

  public loadTradesClick() {
    // only load the trades that have been selected in the grid
    this.strategyDetailService.setTabTradingMode(true);
    const trades: string[] = this.selectedTrades.map(t => t.tradeId);
    //   {
    //   return {
    //     tradeId: t.tradeId,
    //     datetime: this.historicalTradesMeta?.datetime,
    //     timezone: this.historicalTradesMeta?.timezone
    //   };
    // });
    const date = dayjs(this.historicalTradesMeta?.datetime).format(
      'YYYY-MM-DD'
    );
    const time = dayjs(this.historicalTradesMeta?.datetime).format('HH:mm');
    this.tradesCaptureService
      .getTradesByTradeIds(
        trades,
        date,
        time,
        this.historicalTradesMeta?.timezone
      )
      .subscribe(trades => {
        this.tradesLoaded.emit(trades);
      });
  }

  public openLoadHistoricalTrades(event = true) {
    this.isLoadHistoricalTradesOpen = event;
  }

  public openTradeManager(event = true) {
    this.isTradeManagerOpen = event;
    if (!event) {
      this.onSelectionChanged();
    }
  }

  public loadHistoricalTrades(event: {
    data: any;
    meta?: HistoricalTradesMeta;
  }) {
    this.isHistoricalTrades = true;
    if (event.meta) {
      this.historicalTradesMeta = event.meta;
    }
    this.gridApi.setRowData(
      this.entityMapperService.uncleanConfidentialFields(event.data)
    );
    this.applyFilterModel();
  }

  @Debounce(500)
  public loadTrades() {
    this.isHistoricalTrades = false;
    this.historicalTradesMeta = null;
    if (this.selectedTrades?.length > this.maxTrades) {
      return;
    }

    this.gridApi.showLoadingOverlay();

    this.tradesCaptureService
      .getTrades(!this.hasTradesBeenClicked, true)
      .pipe(catchError(this.handleGetTradesError))
      .subscribe({
        next: _ => {
          this.numberOfTradesDisplayed = this.gridApi.getDisplayedRowCount();
        },
        error: (err: 'tradesRestricted' | 'generalError') => {
          if (err === 'tradesRestricted') {
            this.hideTradesTab.emit(true);
          }
        }
      });
  }

  private handleGetTradesError(error: HttpErrorResponse) {
    let msg = 'generalError';
    if (error.status === 403) {
      msg = 'tradesRestricted';
    }
    return throwError(msg);
  }

  public get buttonsEnabled(): boolean {
    return (
      this.selectedTrades?.length > 0 &&
      this.selectedTrades?.length <= this.maxTrades
    );
  }

  public filterChanged(event: AgGridEvent) {
    const filterModel = event.api.getFilterModel();
    this.userStorageService.setItem(TradeFilterStateKey, filterModel);
    this.numberOfTradesDisplayed = this.gridApi.getDisplayedRowCount();
  }

  @Throttle(1_000, { leading: true, trailing: false })
  public hasFilters() {
    const filterModel = this.gridApi?.getFilterModel();
    if (!filterModel) {
      return false;
    }
    return Object.keys(filterModel).length > 0;
  }

  public clearFilters() {
    const filterModel = {};
    this.gridApi.setFilterModel(filterModel);
    this.gridApi.onFilterChanged();
    this.userStorageService.setItem(TradeFilterStateKey, filterModel);
  }

  @Debounce(1000)
  public gridStateChanged(event: AgGridEvent) {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(TradeGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(TradeGridStateKey);
    }
  }

  public resetColumnsState() {
    this.columnApi.resetColumnState();
  }

  public saveDeal() {
    const refs = new Set(this.selectedTrades.map(t => t.dealReference));

    if (refs.size !== 1) {
      this.alertsService.addAlert({
        message: 'Can only save one deal at a time',
        skipLog: true
      });
      return;
    }

    const dealReference = this.selectedTrades[0].dealReference;

    const numTrades = this.trades.filter(
      t => t.dealReference === dealReference
    ).length;

    if (this.selectedTrades.length !== numTrades) {
      this.alertsService.addAlert({
        message: 'Can only save a deal with all of its trades',
        skipLog: true
      });
      return;
    }

    const trades = this.gridApi.getSelectedRows();
    // Modified cells will be strings, so we need to convert them back to numbers
    trades.forEach(t => {
      t.premium = +t.premium;
      t.strike = +t.strike;
    });

    const payload = {
      task: 'saveDeal',
      trades,
      premiumCurrency: this.selectedTrades[0].premiumCurrency
    };

    this.alertsService.addAlert({
      message: `Saving deal ${dealReference}`,
      type: 'success',
      config: {
        timeOut: 1000
      }
    });
    this.rfqService.tradeService(payload).subscribe({
      next: () => {
        this.alertsService.addAlert({
          message: `Deal saved [${dealReference}]`,
          type: 'success',
          config: {
            timeOut: 2000
          }
        });
        this.loadTrades();
      },
      error: err => {
        this.alertsService.addAlert({
          message: `Failed to save deal  [${dealReference}]`
        });
        console.log('Failed to save deal', err);
      },
      complete: () => {}
    });
  }
}
